import { cx } from 'class-variance-authority';
import { ProgressBar, type ProgressBarProps } from 'react-aria-components';

import styles from './progress-circle.module.css';

const SIZE = 20;
const STROKE_WIDTH = 2;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const DASH_LENGTH = CIRCUMFERENCE * 0.25;
const DASH_GAP = CIRCUMFERENCE - DASH_LENGTH;
const CIRCLE_CENTER = SIZE / 2;

export interface ProgressCircleProps
    extends Omit<ProgressBarProps, 'children' | 'className' | 'value' | 'minValue' | 'maxValue' | 'isIndeterminate'> {
    /**
     * Color of the rotating 25% arc. Accepts any CSS color (hex, named,
     * var(...)).
     */
    arcColor?: string;
    /**
     * Color of the static full-circle track behind the arc. Accepts any CSS
     * color.
     */
    trackColor?: string;
    className?: string;
    /** Optional `data-testid` for DOM querying from play functions / e2e. */
    testID?: string;
}

/**
 * Has no visible-label slot, so pass an `aria-label` or `aria-labelledby` for
 * the accessible name (RAC `ProgressBar` warns at runtime if neither is given).
 */
export function ProgressCircle({
    arcColor = 'var(--colorBorderAccent)',
    trackColor = 'var(--colorBorderAccentLight)',
    className,
    testID,
    ...rest
}: ProgressCircleProps) {
    return (
        <ProgressBar isIndeterminate {...rest} className={cx(styles.root, className)} data-testid={testID}>
            <svg aria-hidden className={styles.spin} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE}>
                <circle cx={CIRCLE_CENTER} cy={CIRCLE_CENTER} fill='none' r={RADIUS} stroke={trackColor} strokeWidth={STROKE_WIDTH} />
                <circle
                    cx={CIRCLE_CENTER}
                    cy={CIRCLE_CENTER}
                    fill='none'
                    r={RADIUS}
                    stroke={arcColor}
                    strokeDasharray={`${DASH_LENGTH} ${DASH_GAP}`}
                    strokeLinecap='round'
                    strokeWidth={STROKE_WIDTH}
                    transform={`rotate(-90 ${CIRCLE_CENTER} ${CIRCLE_CENTER})`}
                />
            </svg>
        </ProgressBar>
    );
}
